import React, { useEffect, useState, useRef } from 'react'
import ReactModal from 'react-modal'
import YellowFrame from '../../../assets/early/svg/YellowFrame.svg'
import RedFrame from '../../../assets/early/svg/RedFrame.svg'
import { ReactComponent as Spotlight } from '../../../assets/early/svg/Spotlight.svg'
import { ReactComponent as ConfirmationPlant } from '../../../assets/early/svg/ConfirmationPlant.svg'
import { ReactComponent as PurpleCheck } from '../../../assets/early/svg/PurpleCheck.svg'
import { ReactComponent as PurpleCross } from '../../../assets/early/svg/PurpleCross.svg'
import { format } from 'date-fns'
import { BigAnimalImages } from '../../../assets/early/bigAnimals/BigAnimalImages'
import { useTranslation } from 'react-i18next'
import { respondToDailyQuestion } from '../../../server/server'
import { ANALYTICS_EVENTS } from '../../../analytics/events'
import useTrackedSurface from '../../../analytics/useTrackedSurface'
import { useInstitution } from '../../../auth/InstitutionContext'
import { notify } from '../../../utils/util'
import '../../../styles/early/earlyMotion.css'

const COMMENT_MAX_LENGTH = 500

// How far the artwork moves up to make room for the comment box.
const COMMENT_ART_SHIFT = 50

function ConfirmEmotionModal({ modalVisible, onCancel = () => {}, onConfirm = () => {}, animal = 'tiger', emotion = 'afraid', student = {} }) {
    const [process, setProcess] = useState(false)
    const { t } = useTranslation()
    const { institution } = useInstitution()
    // Schools opt in to letting students explain their emotion (institution setting managed in the admin portal).
    const commentsEnabled = institution?.allowPrimaryEmotionComments === true
    const [comment, setComment] = useState('')
    // Synchronous guard: `process` only updates on the next render, so two quick clicks on "Yes" would both submit.
    const inFlight = useRef(false)
    const artShift = commentsEnabled ? COMMENT_ART_SHIFT : 0
    useTrackedSurface(ANALYTICS_EVENTS.EARLY_CLASSROOM_EMOTION_CONFIRMATION_VIEWED, {
        active: modalVisible,
        contentId: student?.id,
        content: emotion,
    })

    const submit = async () => {
        if (inFlight.current) return
        inFlight.current = true
        setProcess(true)
        const data = {
            emotion: emotion,
            created_date: Date.now(),
            day_code: format(Date.now(), 'yyyy-MM-dd'),
            user_id: student?.id,
            type: 'daily',
        }
        // Stored as `message`, the same field Upstrive Education uses for check-in comments, so teacher views
        // and the AI keyword alerts pick it up. Omitted when empty so it doesn't count as a comment.
        const message = comment.trim()
        if (commentsEnabled && message) data.message = message

        let resp = null
        try {
            resp = await respondToDailyQuestion(data)
        } catch (e) {
            resp = null
        }
        inFlight.current = false
        setProcess(false)
        if (resp?.status === 200) {
            onConfirm()
        } else {
            notify(t('Could not save the emotion. Please try again.'), 'error')
        }
    }

    const cancel = () => {
        if (inFlight.current) return
        onCancel()
    }

    useEffect(() => {
        if (modalVisible) {
            // Every student starts with an empty comment.
            setComment('')
            ReactModal.setAppElement('#root')
            document.getElementById('home').style.filter = 'blur(5px)'
        } else {
            document.getElementById('home').style.filter = 'unset'
        }
        return () => {
            document.getElementById('home').style.filter = 'unset'
        }
    }, [modalVisible])

    return (
        <ReactModal
            transparent
            ariaHideApp={false}
            isOpen={modalVisible}
            onAfterOpen={() => {}}
            onAfterClose={() => {}}
            onRequestClose={cancel}
            className="noScroll"
            portalClassName="ReactModalPortal em-modal-portal"
            closeTimeoutMS={240}
            style={{
                overlay: {
                    backgroundColor: '#00000065',
                    zIndex: 0,
                },
                content: {
                    padding: 0,
                    opacity: 1,
                    width: 600,
                    margin: 'auto',
                    backgroundColor: 'transparent',
                    maxHeight: '100%',
                    border: 'none',
                    boxShadow: 'none',
                    overflow: 'scroll',
                    paddingTop: 35,
                    paddingBottom: 35,
                    position: 'relative',
                },
            }}
        >
            <div className="bg-[#5E639B] border-[10px] border-[#FDDF6D] h-[655px] relative rounded-[60px] mt-[5px] px-[30px] py-[20px] flex flex-col items-center overflow-hidden em-modal-card">
                <Spotlight style={{ position: 'absolute', top: -120 - artShift, left: 50, alignSelf: 'center' }} width={400} />
                <ConfirmationPlant style={{ position: 'absolute', top: 135 - artShift, left: 0 }} />
                <div className="absolute em-modal-hero" style={{ top: 125 - artShift }}>
                    {React.cloneElement(BigAnimalImages[animal][emotion], { width: 200 })}
                </div>
                <p className="text-white text-[36px] leading-[50px] font-bold text-left w-full capitalize em-title-in em-d1">{t(emotion)}</p>
                <div className="flex flex-1 flex-col justify-end w-full">
                    {commentsEnabled && (
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={t('Why do you feel this way? (optional)')}
                            aria-label={t('Why do you feel this way? (optional)')}
                            maxLength={COMMENT_MAX_LENGTH}
                            disabled={process}
                            className="relative w-full h-[100px] mb-3 px-4 py-3 resize-none rounded-[24px] border-[4px] border-[#FDDF6D] bg-white text-[18px] font-semibold text-[#5E639B] placeholder-[#9A9DC4] outline-none focus:border-[#FFBE6D] em-title-in em-d2"
                        />
                    )}
                </div>
                <div className="flex flex-col items-center justify-end em-title-in em-d2">
                    <p className="text-center text-white text-[20px]  font-bold max-w-[70%]">{t('Does this best describe how you’re feeling right now,')}</p>
                    <p className="text-center text-[#FDDF6D] text-[30px]  font-bold mt-2">
                        {student?.first_name} {student?.last_name}?
                    </p>
                </div>
                <div className="flex flex-row items-center justify-center gap-[20px] mt-4 em-title-in em-d3">
                    <button
                        className=" bg-no-repeat bg-contain flex flex-row items-center justify-center gap-[5px] pl-[15px] pr-[11px] em-tap em-tap--card"
                        style={{ backgroundImage: `url(${RedFrame})`, width: 150, height: 75 }}
                        onClick={cancel}
                        disabled={process}
                    >
                        <p className="text-[30px]  font-bold text-[#5E639B]">{t('No')}</p>
                        <PurpleCross />
                    </button>
                    <button
                        className=" bg-no-repeat bg-contain flex flex-row items-center justify-center gap-[5px] pl-[15px] pr-[11px] em-tap em-tap--card"
                        style={{ backgroundImage: `url(${YellowFrame})`, width: 200, height: 75 }}
                        onClick={submit}
                        disabled={process}
                    >
                        <p className="text-[30px] font-bold text-[#5E639B]">{t('Yes')}</p>
                        <PurpleCheck />
                    </button>
                </div>
            </div>
        </ReactModal>
    )
}

export default ConfirmEmotionModal
